<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  UiButton,
  UiChip,
  UiDialog,
  useToaster,
} from "@shopexperts/shared";
import type { ChatMessage, ProjectSide, RawQuoteRecord } from "@/v2Projects/types";
import type { QuoteMessageCardMetric, QuoteMessageCardTimelineStep } from "./QuoteMessageCard.types";
import QuoteMessageCardDialog from "./QuoteMessageCardDialog.vue";
import { reportProjectUserError } from "@/v2Projects/userErrors";
import {
  calculateDeliveryDeadline,
  formatCompactDate,
  formatCurrency,
  getDeliveryWindowLabel,
} from "@/v2Projects/utils/display";
import {
  getQuoteCardStatusBadgeColors,
  getQuoteCardStatusLabel,
  getQuotePaymentUrl,
  isClientQuoteActionable,
  isInternalQuotePayment,
} from "@/v2Projects/utils/quotePresentation";
import { quoteCopy } from "@/v2Projects/utils/quoteCopy";

const props = defineProps<{
  message: ChatMessage;
  side: ProjectSide;
  isOwn: boolean;
  senderName: string;
  senderAvatar?: string;
  senderInitials?: string;
  expertRole?: string | null;
  quote?: RawQuoteRecord | null;
  highlightClass?: string;
  autoOpenDetails?: boolean;
  rejectQuote?: (quoteId: number) => Promise<void>;
  viewQuote?: (quoteId: number) => Promise<void>;
}>();

const toaster = useToaster();
const isProposalDetailsOpen = ref(false);
const isPaymentDialogOpen = ref(false);
const localClientViewedAt = ref<string | null>(null);

const effectiveQuoteScope = computed(() => props.quote?.scope ?? props.message.scope ?? null);
const hasQuoteScopeHtml = computed(() => /<\/?[a-z][\s\S]*>/i.test(effectiveQuoteScope.value ?? ""));
const effectiveQuotePrice = computed(() => {
  if (props.quote) {
    return {
      amount_in_cents: props.quote.amount_cents,
      currency: props.quote.currency,
      quantity: props.quote.quantity,
      interval: props.quote.interval,
    };
  }
  return props.message.price ?? null;
});

const quoteStrip = computed(() => {
  if (!effectiveQuotePrice.value) return null;
  return {
    amount_cents: effectiveQuotePrice.value.amount_in_cents,
    currency: effectiveQuotePrice.value.currency,
    quantity: effectiveQuotePrice.value.quantity,
    interval: effectiveQuotePrice.value.interval,
    delivery_after_days: props.quote?.delivery_after_days ?? props.message.delivery_after_days ?? null,
    includes_weekends: props.quote?.includes_weekends ?? props.message.includes_weekends ?? null,
    hours_bank_expires_after_days:
      props.quote?.hours_bank_expires_after_days ??
      props.message.latest_hours_bank_expires_after_days ??
      props.message.hours_bank_expires_after_days ??
      null,
    hours_bank_expiration_terms:
      props.quote?.hours_bank_expiration_terms ??
      props.message.latest_hours_bank_expiration_terms ??
      props.message.hours_bank_expiration_terms ??
      null,
  };
});

const effectiveQuoteStatus = computed(
  () => props.quote?.status ?? props.message.latest_status ?? props.message.status,
);
const effectiveQuotePaymentStatus = computed(
  () => props.quote?.payment_status ?? props.message.latest_payment_status ?? props.message.payment_status,
);
const effectiveExternalPaymentUrl = computed(
  () => props.quote?.external_payment_url ?? props.message.latest_external_payment_url ?? props.message.external_payment_url,
);
const effectivePaymentUrl = computed(() =>
  getQuotePaymentUrl({
    id: props.quote?.id,
    quote_id: props.message.quote_id,
    payment_mode: props.quote?.payment_mode,
    type: props.message.type,
    payment_provider:
      props.quote?.payment_provider ??
      props.message.latest_payment_provider ??
      props.message.payment_provider,
    payment_url: props.quote?.payment_url ?? props.message.latest_payment_url ?? props.message.payment_url,
    provider_payment_url:
      props.quote?.provider_payment_url ??
      props.message.latest_provider_payment_url ??
      props.message.provider_payment_url,
    external_payment_url: effectiveExternalPaymentUrl.value,
  }),
);
const effectivePaymentMode = computed(() => props.quote?.payment_mode ?? props.message.type);
const isInternalPayment = computed(() => isInternalQuotePayment(effectivePaymentMode.value));
const isPaidQuote = computed(() => effectiveQuotePaymentStatus.value === "paid");
const isActionableQuote = computed(() =>
  isClientQuoteActionable({
    status: effectiveQuoteStatus.value ?? "",
    payment_status: effectiveQuotePaymentStatus.value ?? null,
  }),
);

const showAcceptQuote = computed(
  () =>
    props.side === "client" &&
    isActionableQuote.value &&
    !!effectivePaymentUrl.value,
);

const showRejectQuote = computed(
  () =>
    props.side === "client" &&
    isActionableQuote.value &&
    typeof props.message.quote_id === "number" &&
    !!props.rejectQuote,
);

const quoteCardHeadline = computed(() => {
  if (props.side === "client" && !props.isOwn) {
    return quoteCopy.proposalDetails.cardHeadlineReceived(props.senderName);
  }
  if (props.side === "client") {
    return quoteCopy.proposalDetails.cardHeadlineSent(props.senderName);
  }
  return quoteCopy.proposalDetails.cardHeadlineShared(props.senderName);
});

const quoteCardStatusLabel = computed(() =>
  getQuoteCardStatusLabel({
    status: effectiveQuoteStatus.value,
    payment_status: effectiveQuotePaymentStatus.value,
  }),
);

const quoteCardStatusBadgeColors = computed(() =>
  getQuoteCardStatusBadgeColors({
    status: effectiveQuoteStatus.value,
    payment_status: effectiveQuotePaymentStatus.value,
  }),
);

const submittedDateSource = computed(() =>
  props.quote?.created_at ?? new Date(props.message._creationTime).toISOString(),
);
const quoteSubmittedDate = computed(() => formatCompactDate(submittedDateSource.value));

const quoteEstimatedDeliveryDate = computed(() => {
  const days = quoteStrip.value?.delivery_after_days;
  if (days == null) return null;
  const deadline = calculateDeliveryDeadline(
    new Date(submittedDateSource.value),
    days,
    quoteStrip.value?.includes_weekends ?? false,
  );
  return formatCompactDate(deadline.toISOString());
});

const quoteEstimatedDeliveryDetail = computed(() => {
  const days = quoteStrip.value?.delivery_after_days;
  if (days == null) return null;
  const deliveryWindow = getDeliveryWindowLabel(days, quoteStrip.value?.includes_weekends ?? false);

  if (isPaidQuote.value) {
    return deliveryWindow;
  }

  return `${deliveryWindow} from the day of purchase. The date shown is an estimate based on today.`;
});

const quoteInterval = computed(() => effectiveQuotePrice.value?.interval ?? null);
const isHourlyQuote = computed(() => quoteInterval.value === "hourly");
const isHoursBankQuote = computed(() => quoteInterval.value === "hours_bank");
const hasMultipleUnits = computed(() => (effectiveQuotePrice.value?.quantity ?? 0) > 1);

const quotePriceLabel = computed(() => {
  if (isHoursBankQuote.value) return quoteCopy.totalLabels.hours_bank;
  if (isHourlyQuote.value) return quoteCopy.totalLabels.hourly;
  if (hasMultipleUnits.value) return quoteCopy.totalLabels.one_time;
  return quoteCopy.rateLabels.one_time.single;
});

const quotePriceValue = computed(() => {
  if (!effectiveQuotePrice.value) return null;
  return formatCurrency(
    effectiveQuotePrice.value.amount_in_cents * effectiveQuotePrice.value.quantity,
    effectiveQuotePrice.value.currency,
  );
});

const quoteRateValue = computed(() => {
  if (!effectiveQuotePrice.value) return null;
  return formatCurrency(effectiveQuotePrice.value.amount_in_cents, effectiveQuotePrice.value.currency);
});

const quoteRateBreakdown = computed(() => {
  if (!effectiveQuotePrice.value) return null;
  const { amount_in_cents, currency, quantity, interval } = effectiveQuotePrice.value;
  if (interval === "one_time" && quantity <= 1) return null;
  const rate = formatCurrency(amount_in_cents, currency);
  const isHours = interval === "hourly" || interval === "hours_bank";
  const unit = isHours ? "hr" : quantity === 1 ? "deliverable" : "deliverables";
  if (isHours) return `${quantity} hours x ${rate}/${unit}`;
  return `${quantity} ${unit} x ${rate}`;
});

const quoteQuantityCell = computed(() => {
  if (!effectiveQuotePrice.value) return null;
  const { interval, quantity } = effectiveQuotePrice.value;
  if (interval === "one_time" && quantity <= 1) return null;
  if (isHoursBankQuote.value) {
    return { label: quoteCopy.quantityLabels.hours_bank.stripLabel, value: `${quantity} ${quantity === 1 ? "hour" : "hours"}` };
  }
  if (isHourlyQuote.value) {
    return { label: quoteCopy.quantityLabels.hourly.stripLabel, value: `${quantity} ${quantity === 1 ? "hour" : "hours"}` };
  }
  return {
    label: quoteCopy.quantityLabels.one_time.stripLabel,
    value: `${quantity} ${quantity === 1 ? "deliverable" : "deliverables"}`,
  };
});

const quoteHoursBankExpirationDays = computed(() => {
  if (!isHoursBankQuote.value) return null;
  return quoteStrip.value?.hours_bank_expires_after_days ?? null;
});

const quoteHoursBankExpirationValue = computed(() => {
  const days = quoteHoursBankExpirationDays.value;
  if (days == null) return null;
  return `${days} ${days === 1 ? "day" : "days"}`;
});

const effectiveClientViewedAt = computed(
  () =>
    props.quote?.client_viewed_at ??
    props.message.latest_client_viewed_at ??
    props.message.client_viewed_at ??
    localClientViewedAt.value,
);

const invoiceUrl = computed(
  () =>
    props.quote?.provider_invoice_url ??
    props.message.latest_provider_invoice_url ??
    props.message.provider_invoice_url ??
    props.quote?.invoice_url ??
    props.message.latest_invoice_url ??
    props.message.invoice_url ??
    null,
);

const priceTimelineCards = computed<QuoteMessageCardMetric[]>(() => {
  const price = effectiveQuotePrice.value;
  const submittedCard = {
    label: quoteCopy.proposalDetails.submittedLabel,
    value: quoteSubmittedDate.value,
    detail: null,
  };
  const deliveryCard = quoteEstimatedDeliveryDate.value
    ? {
        label: quoteCopy.proposalDetails.estimatedDeliveryLabel,
        value: quoteEstimatedDeliveryDate.value,
        detail: quoteEstimatedDeliveryDetail.value,
        valueIsEstimate: !isPaidQuote.value,
      }
    : null;

  if (!price) return [submittedCard];

  if (isHoursBankQuote.value) {
    return [
      {
        label: quoteCopy.totalLabels.hours_bank,
        value: quotePriceValue.value ?? quoteCopy.proposalDetails.notAvailable,
        detail: quoteRateValue.value
          ? `${quoteRateValue.value} ${quoteCopy.proposalDetails.hourlyRateDetail}`
          : null,
      },
      submittedCard,
      {
        label: quoteCopy.quantityLabels.hours_bank.stripLabel,
        value: quoteQuantityCell.value?.value ?? quoteCopy.proposalDetails.notAvailable,
        detail: quoteHoursBankExpirationValue.value
          ? `${quoteCopy.proposalDetails.validForPrefix} ${quoteHoursBankExpirationValue.value}`
          : null,
      },
    ];
  }

  if (isHourlyQuote.value) {
    const cards = [
      {
        label: quoteCopy.rateLabels.hourly,
        value: quoteRateValue.value ?? quoteCopy.proposalDetails.notAvailable,
        detail: quoteCopy.proposalDetails.hourlyRateDetail,
      },
      {
        label: quoteCopy.totalLabels.hourly,
        value: quotePriceValue.value ?? quoteCopy.proposalDetails.notAvailable,
        detail: quoteQuantityCell.value ? `${quoteQuantityCell.value.label}: ${quoteQuantityCell.value.value}` : null,
      },
    ];
    return deliveryCard ? [...cards, deliveryCard] : cards;
  }

  const cards = [
    {
      label: quotePriceLabel.value,
      value: quotePriceValue.value ?? quoteCopy.proposalDetails.notAvailable,
      detail: quoteRateBreakdown.value,
    },
    submittedCard,
  ];

  return deliveryCard ? [...cards, deliveryCard] : cards;
});

const proposalTimelineSteps = computed<QuoteMessageCardTimelineStep[]>(() => {
  const paidAtDate = isPaidQuote.value
    ? formatCompactDate(props.quote?.status_changed_at ?? submittedDateSource.value)
    : null;

  // When the quote has been paid but we never recorded an explicit `client_viewed_at`,
  // we infer the review happened no later than the payment and use that timestamp.
  const reviewedDateSource = effectiveClientViewedAt.value
    ? formatCompactDate(effectiveClientViewedAt.value)
    : paidAtDate;

  return [
    {
      label: quoteCopy.proposalDetails.timelineSubmitted,
      value: quoteSubmittedDate.value,
      complete: true,
    },
    {
      label: quoteCopy.proposalDetails.timelineViewed,
      value: reviewedDateSource ?? quoteCopy.proposalDetails.upcomingLabel,
      complete: !!effectiveClientViewedAt.value || isPaidQuote.value,
    },
    {
      label: quoteCopy.proposalDetails.timelinePaymentCompleted,
      value: paidAtDate ?? quoteCopy.proposalDetails.pendingLabel,
      complete: isPaidQuote.value,
    },
    {
      label: quoteCopy.proposalDetails.timelineKickoff,
      value: isPaidQuote.value
        ? quoteCopy.proposalDetails.kickoffReadyLabel
        : quoteCopy.proposalDetails.upcomingLabel,
      complete: false,
    },
  ];
});

function formatTimestamp(ts: number): string {
  const date = new Date(ts);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (isToday) {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function openPaymentDialog() {
  if (!effectivePaymentUrl.value) return;
  if (isInternalPayment.value) {
    window.open(effectivePaymentUrl.value, "_blank", "noopener,noreferrer");
    return;
  }
  isPaymentDialogOpen.value = true;
}

function confirmExternalPayment() {
  if (!effectivePaymentUrl.value) return;
  window.open(effectivePaymentUrl.value, "_blank", "noopener,noreferrer");
  isPaymentDialogOpen.value = false;
}

function openInvoice() {
  if (!invoiceUrl.value) return;
  window.open(invoiceUrl.value, "_blank", "noopener,noreferrer");
}

function handleInvoiceCopySuccess() {
  toaster.create({ title: quoteCopy.proposalDetails.copiedToast, type: "success" });
}

async function openProposalDetails() {
  isProposalDetailsOpen.value = true;
  if (props.side !== "client" || typeof props.message.quote_id !== "number" || !props.viewQuote) return;
  if (effectiveClientViewedAt.value) return;

  try {
    await props.viewQuote(props.message.quote_id);
    localClientViewedAt.value = new Date().toISOString();
  } catch (error) {
    reportProjectUserError("viewQuote", error, {
      side: props.side,
      extra: {
        quoteId: props.message.quote_id,
        messageId: props.message._id,
      },
    });
  }
}

watch(
  () => props.autoOpenDetails,
  (shouldOpen) => {
    if (shouldOpen) {
      void openProposalDetails();
    }
  },
  { immediate: true },
);

async function handleRejectQuote() {
  if (!props.rejectQuote || typeof props.message.quote_id !== "number") return;
  try {
    await props.rejectQuote(props.message.quote_id);
    toaster.create({ title: quoteCopy.proposalDetails.rejectedToast, type: "success" });
    isProposalDetailsOpen.value = false;
  } catch (error) {
    const message = reportProjectUserError("rejectQuote", error, {
      side: props.side,
      extra: {
        quoteId: props.message.quote_id,
        messageId: props.message._id,
      },
    });
    toaster.create({ title: message.title, description: message.description, type: "error" });
  }
}
</script>

<template>
  <div
    class="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 max-sm:rounded-lg"
    :class="highlightClass"
  >
    <div class="flex flex-col gap-5 px-5 py-4">
      <div class="flex items-start justify-between gap-3 max-sm:flex-col-reverse max-sm:gap-2">
        <div class="min-w-0">
          <p class="truncate text-[15px] font-semibold leading-snug text-gray-900 max-sm:whitespace-normal max-sm:overflow-visible max-sm:text-clip">
            {{ quoteCardHeadline }}
          </p>
          <span class="mt-0.5 block text-h5 text-gray-400">
            {{ formatTimestamp(message._creationTime) }}
          </span>
        </div>
        <UiChip
          size="tag"
          variant="plain"
          :text="quoteCardStatusLabel"
          :bg-color-class="quoteCardStatusBadgeColors.bgClass"
          :text-color-class="quoteCardStatusBadgeColors.textClass"
          class="shrink-0 max-sm:self-start"
        />
      </div>

      <div class="flex flex-wrap items-end justify-between gap-x-6 gap-y-4 max-sm:flex-col max-sm:flex-nowrap max-sm:items-stretch max-sm:gap-4">
        <div class="flex flex-wrap items-start gap-x-10 gap-y-3 max-sm:grid max-sm:grid-cols-2 max-sm:gap-x-4 max-sm:gap-y-3">
          <div v-if="quotePriceValue" class="min-w-0">
            <span class="block text-h5 leading-tight text-gray-500">{{ quotePriceLabel }}</span>
            <span class="mt-1 block text-[15px] font-semibold leading-tight text-gray-900">
              {{ quotePriceValue }}
            </span>
            <span
              v-if="quoteRateBreakdown"
              class="mt-0.5 block text-[12px] leading-tight text-gray-400"
            >
              {{ quoteRateBreakdown }}
            </span>
          </div>
          <div v-if="quoteQuantityCell" class="min-w-0">
            <span class="block text-h5 leading-tight text-gray-500">{{ quoteQuantityCell.label }}</span>
            <span class="mt-1 block text-[15px] font-semibold leading-tight text-gray-900">
              {{ quoteQuantityCell.value }}
            </span>
          </div>
          <div class="min-w-0">
            <span class="block text-h5 leading-tight text-gray-500">{{ quoteCopy.proposalDetails.submittedLabel }}</span>
            <span class="mt-1 block text-[15px] font-semibold leading-tight text-gray-900">
              {{ quoteSubmittedDate }}
            </span>
          </div>
          <div v-if="quoteEstimatedDeliveryDate" class="min-w-0">
            <span class="block text-h5 leading-tight text-gray-500">{{ quoteCopy.proposalDetails.estimatedDeliveryLabel }}</span>
            <span class="mt-1 block text-[15px] font-semibold leading-tight text-gray-900">
              {{ quoteEstimatedDeliveryDate }}
            </span>
          </div>
          <div v-if="quoteHoursBankExpirationValue" class="min-w-0">
            <span class="block text-h5 leading-tight text-gray-500">{{ quoteCopy.hoursBankExpirationDisplayLabel }}</span>
            <span class="mt-1 block text-[15px] font-semibold leading-tight text-gray-900">
              {{ quoteHoursBankExpirationValue }}
            </span>
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-end gap-3 max-sm:flex-col-reverse max-sm:items-stretch max-sm:gap-2">
          <button
            type="button"
            class="text-h4 font-medium text-gray-700 underline underline-offset-[3px] decoration-[1.5px] transition hover:text-gray-950 max-sm:py-1 max-sm:text-center"
            @click="openProposalDetails"
          >
            {{ quoteCopy.proposalDetails.viewDetailsAction }}
          </button>

          <div
            v-if="isPaidQuote || showAcceptQuote || showRejectQuote"
            class="flex flex-wrap items-center gap-2 max-sm:flex-col max-sm:items-stretch max-sm:gap-2"
          >
            <UiButton
              v-if="isPaidQuote"
              variant="primary"
              :label="quoteCopy.proposalDetails.openInvoiceAction"
              :disabled="!invoiceUrl"
              class="max-sm:w-full"
              @click="openInvoice"
            />
            <template v-else>
              <UiButton
                v-if="showRejectQuote"
                variant="outline"
                :label="quoteCopy.proposalDetails.rejectAction"
                class="max-sm:w-full"
                @click="handleRejectQuote"
              />
              <UiButton
                v-if="showAcceptQuote"
                variant="primary"
                :label="quoteCopy.proposalDetails.payNowAction"
                class="max-sm:w-full"
                @click="openPaymentDialog"
              />
            </template>
          </div>
        </div>
      </div>
    </div>

    <QuoteMessageCardDialog
      :open="isProposalDetailsOpen"
      :status-label="quoteCardStatusLabel"
      :status-bg-class="quoteCardStatusBadgeColors.bgClass"
      :status-text-class="quoteCardStatusBadgeColors.textClass"
      :sender-name="senderName"
      :sender-avatar="senderAvatar"
      :sender-initials="senderInitials"
      :expert-role="expertRole"
      :metric-cards="priceTimelineCards"
      :scope="effectiveQuoteScope"
      :scope-is-html="hasQuoteScopeHtml"
      :expiration-terms="quoteStrip?.hours_bank_expiration_terms"
      :timeline-steps="proposalTimelineSteps"
      :is-paid-quote="isPaidQuote"
      :invoice-url="invoiceUrl"
      :show-accept-quote="showAcceptQuote"
      :show-reject-quote="showRejectQuote"
      @close="isProposalDetailsOpen = false"
      @reject="handleRejectQuote"
      @accept="openPaymentDialog"
      @open-invoice="openInvoice"
      @copy-invoice-success="handleInvoiceCopySuccess"
    />

    <UiDialog
      v-model="isPaymentDialogOpen"
      :title="quoteCopy.proposalDetails.externalPaymentDialogTitle"
      :text="quoteCopy.proposalDetails.externalPaymentDialogText"
      :primary-action-label="quoteCopy.proposalDetails.externalPaymentContinueAction"
      :secondary-action-label="quoteCopy.proposalDetails.cancelAction"
      @primary-action="confirmExternalPayment"
      @secondary-action="isPaymentDialogOpen = false"
    />
  </div>
</template>
